import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FaPlus, FaEdit, FaTrash, FaChartBar, FaPlay, FaStop, FaPaperPlane, FaEye, FaFlask } from 'react-icons/fa';
import api from '../../services/api';
import { useAppSelector } from '../../store/hooks';
import { obterIdTenent } from '../../services/seguranca';
import { IdEmpresaAdm } from '../../services/utilitarios';
import { Redirect } from 'react-router-dom';
import { LocaSmartLayout } from '../../components/locasmart-layout';
import PesquisaFormModal from './PesquisaFormModal';
import PesquisaResultadosModal from './PesquisaResultadosModal';
import PesquisaTesteModal from './PesquisaTesteModal';

interface PesquisaDto {
  id: string;
  titulo: string;
  descricao: string;
  tipo: number;
  status: number;
  dataCriacao: string;
  dataExpiracao: string | null;
  idEmpresaCriadora: string;
  perguntas: PerguntaDto[];
  totalRespostas: number;
}

interface PerguntaDto {
  id: string;
  idPesquisa: string;
  texto: string;
  tipoPergunta: number;
  opcoes: string;
  ordem: number;
  obrigatoria: boolean;
}

const tiposLabel: Record<number, string> = { 0: 'Satisfacao', 1: 'Mercado', 2: 'Feedback', 3: 'NPS', 4: 'Outro' };
const statusLabel: Record<number, string> = { 0: 'Rascunho', 1: 'Ativa', 2: 'Encerrada' };
const statusColor: Record<number, string> = { 0: '#94a3b8', 1: '#22c55e', 2: '#ef4444' };

// ============ STYLES ============
const Container = styled.div`
  padding: 30px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BtnPrimary = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: 10px;
  background: #3b82f6;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: #2563eb; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 20px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  transition: box-shadow 0.2s;
  &:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.08); }
`;

const CardHeader = styled.div`
  padding: 20px 20px 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const CardBody = styled.div`
  padding: 12px 20px 20px;
`;

const CardTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 4px;
`;

const CardDesc = styled.p`
  font-size: 0.85rem;
  color: #64748b;
  margin: 0 0 12px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Badge = styled.span<{ color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 600;
  background: ${p => p.color}18;
  color: ${p => p.color};
`;

const MetaRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const MetaItem = styled.span`
  font-size: 0.8rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  border-top: 1px solid #f1f5f9;
  padding: 12px 20px;
`;

const ActionBtn = styled.button<{ variant?: 'danger' | 'success' | 'warning' | 'info' }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  color: ${p => {
    if (p.variant === 'danger') return '#ef4444';
    if (p.variant === 'success') return '#22c55e';
    if (p.variant === 'warning') return '#f59e0b';
    if (p.variant === 'info') return '#3b82f6';
    return '#475569';
  }};
  &:hover {
    background: ${p => {
      if (p.variant === 'danger') return '#fef2f2';
      if (p.variant === 'success') return '#f0fdf4';
      if (p.variant === 'warning') return '#fffbeb';
      if (p.variant === 'info') return '#eff6ff';
      return '#f8fafc';
    }};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #94a3b8;
  font-size: 1rem;
`;

const PesquisasPage: React.FC = () => {
  const token = useAppSelector(state => state.token);
  const tenantId = obterIdTenent(token);

  const [pesquisas, setPesquisas] = useState<PesquisaDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPesquisa, setEditingPesquisa] = useState<PesquisaDto | null>(null);
  const [showResultados, setShowResultados] = useState(false);
  const [resultadosPesquisaId, setResultadosPesquisaId] = useState<string>('');
  const [testePesquisa, setTestePesquisa] = useState<PesquisaDto | null>(null);

  const carregarPesquisas = useCallback(async () => {
    try {
      const res = await api.get('/Pesquisas/');
      setPesquisas(res.data);
    } catch {
      toast.error('Erro ao carregar pesquisas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregarPesquisas(); }, [carregarPesquisas]);

  if (tenantId !== IdEmpresaAdm) return <Redirect to="/home" />;

  const handleCriar = () => {
    setEditingPesquisa(null);
    setShowForm(true);
  };

  const handleEditar = (pesquisa: PesquisaDto) => {
    setEditingPesquisa(pesquisa);
    setShowForm(true);
  };

  const handleRemover = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta pesquisa?')) return;
    try {
      await api.delete(`/Pesquisas/${id}`);
      toast.success('Pesquisa removida');
      carregarPesquisas();
    } catch {
      toast.error('Erro ao remover pesquisa');
    }
  };

  const handleAlterarStatus = async (id: string, novoStatus: number) => {
    try {
      await api.put(`/PesquisaStatus/${id}/${novoStatus}`);
      toast.success(`Status alterado`);
      carregarPesquisas();
    } catch {
      toast.error('Erro ao alterar status');
    }
  };

  const handleVerResultados = (id: string) => {
    setResultadosPesquisaId(id);
    setShowResultados(true);
  };

  const handleFormClose = (saved: boolean) => {
    setShowForm(false);
    setEditingPesquisa(null);
    if (saved) carregarPesquisas();
  };

  const formatDate = (d: string) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('pt-BR');
  };

  return (
    <LocaSmartLayout>
      <Container>
        <Header>
          <Title><FaChartBar color="#3b82f6" /> Pesquisas Internas</Title>
          <BtnPrimary onClick={handleCriar}><FaPlus /> Nova Pesquisa</BtnPrimary>
        </Header>

        {loading ? (
          <EmptyState>Carregando...</EmptyState>
        ) : pesquisas.length === 0 ? (
          <EmptyState>
            Nenhuma pesquisa criada ainda.<br />
            Crie sua primeira pesquisa para coletar feedback dos seus clientes.
          </EmptyState>
        ) : (
          <Grid>
            {pesquisas.map(p => (
              <Card key={p.id}>
                <CardHeader>
                  <div>
                    <CardTitle>{p.titulo}</CardTitle>
                    <Badge color={statusColor[p.status]}>{statusLabel[p.status]}</Badge>
                  </div>
                  <Badge color="#6366f1">{tiposLabel[p.tipo]}</Badge>
                </CardHeader>
                <CardBody>
                  <CardDesc>{p.descricao || 'Sem descricao'}</CardDesc>
                  <MetaRow>
                    <MetaItem>Criada: {formatDate(p.dataCriacao)}</MetaItem>
                    {p.dataExpiracao && <MetaItem>Expira: {formatDate(p.dataExpiracao)}</MetaItem>}
                    <MetaItem>{p.perguntas?.length || 0} perguntas</MetaItem>
                    <MetaItem><strong>{p.totalRespostas}</strong> respostas</MetaItem>
                  </MetaRow>
                </CardBody>
                <CardActions>
                  {p.status === 0 && (
                    <>
                      <ActionBtn onClick={() => handleEditar(p)}><FaEdit /> Editar</ActionBtn>
                      <ActionBtn variant="info" onClick={() => setTestePesquisa(p)}><FaFlask /> Testar</ActionBtn>
                      <ActionBtn variant="success" onClick={() => handleAlterarStatus(p.id, 1)}><FaPlay /> Ativar</ActionBtn>
                      <ActionBtn variant="danger" onClick={() => handleRemover(p.id)}><FaTrash /></ActionBtn>
                    </>
                  )}
                  {p.status === 1 && (
                    <>
                      <ActionBtn variant="info" onClick={() => setTestePesquisa(p)}><FaFlask /> Testar</ActionBtn>
                      <ActionBtn variant="warning" onClick={() => handleAlterarStatus(p.id, 2)}><FaStop /> Encerrar</ActionBtn>
                      <ActionBtn variant="info" onClick={() => handleVerResultados(p.id)}><FaChartBar /> Resultados</ActionBtn>
                    </>
                  )}
                  {p.status === 2 && (
                    <ActionBtn variant="info" onClick={() => handleVerResultados(p.id)}><FaChartBar /> Resultados</ActionBtn>
                  )}
                </CardActions>
              </Card>
            ))}
          </Grid>
        )}

        {showForm && (
          <PesquisaFormModal
            pesquisa={editingPesquisa}
            onClose={handleFormClose}
          />
        )}

        {showResultados && (
          <PesquisaResultadosModal
            pesquisaId={resultadosPesquisaId}
            onClose={() => setShowResultados(false)}
          />
        )}

        {testePesquisa && (
          <PesquisaTesteModal
            pesquisa={testePesquisa}
            onClose={() => setTestePesquisa(null)}
          />
        )}
      </Container>
    </LocaSmartLayout>
  );
};

export default PesquisasPage;
